<script setup lang="ts">
import { dollars } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';

import AppliedDiscountsPopper from '@/components/base/AppliedDiscountsPopper.vue';
import InfoIcon from '@/components/base/assets/InfoIcon.vue';
import PopperWrapper from '@/components/base/layout/PopperWrapper.vue';
import DiscountCode from '@/components/checkout/summary/DiscountCode.vue';
import { useDiscount } from '@/composables/useDiscount';
import { PaymentItem, usePayment } from '@/composables/usePayment';
import money from '@/filters/money';
import { adaptSavings } from '@/utils/cart';

const store = useStore();

const { availableDiscountCodes } = useDiscount(store);
const { estimatedAmountToCharge, paymentItems, totalSavings } = usePayment(store, ref(false));
const loading = ref(false);
const discountErrorMessage = ref('');

const savingsBreakdown = computed(() =>
  totalSavings.value ? adaptSavings(totalSavings.value) : [],
);

const setDiscountCodeErrorMessage = (errorMessage: string) => {
  discountErrorMessage.value = errorMessage;
};

const removeDiscountCodeErrorMessage = () => {
  discountErrorMessage.value = '';
};

const formatAmount = ({ label, amount }: PaymentItem) => {
  if (!amount) {
    return 'TBD';
  }
  if (amount.centAmount === 0 && (label === 'Shipping' || label === 'Heat-Resistant Packaging')) {
    return 'FREE';
  }
  return money(amount);
};
const infoIconFilled = nutshell['img/info-icon-filled.svg'];
</script>

<template>
  <div
    class="p-4 bg-white md:mb-5 md:px-10 md:py-7 md:border md:border-solid md:border-gray-300 md:rounded-2xl"
    :class="{ 'opacity-50 pointer-events-none': loading }"
    data-test="order-payments-items-section"
  >
    <h2 class="visible hidden text-xl leading-5 md:block">Order Summary</h2>
    <div v-if="availableDiscountCodes.length" class="text-sm">
      <div class="mt-1 font-semibold font-sofia-pro">Promos</div>
      <DiscountCode
        v-for="discountCode in availableDiscountCodes"
        :key="discountCode.label"
        :discountCode="discountCode"
        @remove-error-message="removeDiscountCodeErrorMessage"
        @set-error-message="setDiscountCodeErrorMessage"
        v-model:loading="loading"
        class="mt-2"
      />
      <div v-if="discountErrorMessage" class="text-red-700">
        {{ discountErrorMessage }}
      </div>
    </div>
    <div
      :class="{
        'border-gray-200 border-solid border-t-2 mt-4 md:mt-8': availableDiscountCodes.length,
      }"
    >
      <div
        v-for="item in paymentItems"
        :key="item.label"
        class="flex justify-between gap-4 mt-1 text-sm"
        :class="item.label === 'Subtotal' ? 'font-bold md:mt-6' : ''"
      >
        <div class="flex">
          <div class="flex items-center">
            {{ item.detail || item.label }}
          </div>
          <div
            v-if="item.label === 'Temporary Surcharge'"
            class="inline-block tiny-note popover-container"
          >
            <PopperWrapper arrow class="legacy-max-width" offsetDistance="10" placement="bottom">
              <template v-slot:content>
                <div class="p-3 text-xs text-neutral-500" data-test="surcharge-popper">
                  <p>
                    This temporary surcharge will help us keep our high quality and service amidst
                    rising inflation &amp; fuel costs.
                    <a href="https://help.nuts.com/en_us/fuel-surcharge-S175W3Dtc" target="_blank"
                      >Learn more</a
                    >
                  </p>
                </div>
              </template>
              <img
                role="button"
                alt="surcharge info tooltip"
                class="cursor-pointer ml-1 transform -translate-y-0.5"
                :src="infoIconFilled"
              />
            </PopperWrapper>
            <span class="sr-only"> surcharge details are shown in a tooltip </span>
          </div>
          <div
            v-if="item.label === 'Duties' && dollars(item.amount)"
            class="inline-block tiny-note popover-container"
          >
            <PopperWrapper arrow class="legacy-max-width" offsetDistance="10" placement="bottom">
              <template v-slot:content>
                <div class="p-3 text-xs text-neutral-500" data-test="duties-popper">
                  <p>This fee is due to recent tariffs imposed on imported goods.</p>
                </div>
              </template>
              <img
                role="button"
                alt="duties info tooltip"
                class="cursor-pointer ml-1 transform -translate-y-0.5"
                :src="infoIconFilled"
              />
            </PopperWrapper>
            <span class="sr-only"> duties details are shown in a tooltip </span>
          </div>
        </div>
        <div>{{ formatAmount(item) }}</div>
      </div>
      <div
        class="flex justify-between pt-4 mt-4 text-base font-bold border-t border-solid border-neutral-300 md:mt-7"
      >
        <div>Total</div>
        <div>{{ money(estimatedAmountToCharge) }}</div>
      </div>
      <div v-if="totalSavings" class="flex justify-between gap-4 mt-1 text-sm">
        <div class="flex items-center">
          Total Savings
          <AppliedDiscountsPopper :discounts="savingsBreakdown" heading="Savings Explained">
            <InfoIcon alt="Total Savings Info Icon" :size="16" />
          </AppliedDiscountsPopper>
        </div>
        <div>
          {{ money(totalSavings.value) }}
        </div>
      </div>
    </div>
  </div>
</template>
